import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { Step, type ForumFormData, type EmergencyContact } from "../types/forum";
import { SCHOOL_AGES, RELATIONSHIPS } from "../constants/forum";
import { t, tfn, type ForumLang } from "../constants/forumTranslations";
import { submitRegistration, getClassesForForum, getRegistrationForRestore, getCurrentClassesByStudentCode, checkStudentCodeAvailable, getActiveRegistrationMonth, getAdminSettings } from "../lib/api";
import type { SubmitRegistrationRequest, ForumClassFromApi, AdminSettings } from "../types";
import { VisaLogo, MastercardLogo, AzulLogo } from "../components/PaymentLogos";
import { PolicyModal } from "../components/PolicyModal";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../lib/firebase";

const LANG_STORAGE_KEY = "forumLang";
const LANGUAGE_CHANGE_MS = 500;

const BRAND = {
  navy: "#161b3d",
  maroon: "#8b1e22",
  navyLight: "#2a2f55",
  maroonLight: "#a62d31",
};

const MONTH_NAMES_ES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const MONTH_NAMES_EN = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

function formatRegistrationMonthLabel(yyyyMm: string, lang: ForumLang): string {
  const [y, m] = yyyyMm.split("-").map(Number);
  if (!y || !m || m < 1 || m > 12) return yyyyMm;
  const names = lang === "es" ? MONTH_NAMES_ES : MONTH_NAMES_EN;
  return `${names[m - 1]} ${y}`;
}

/** Forum form typography: same sizes as admin, premium Apple-style */
const FORUM = {
  sectionTitle: "text-[17px] font-semibold text-[#1d1d1f] tracking-tight",
  sectionSubtitle: "text-[13px] text-[#86868b] font-normal mt-0.5",
  label: "block text-[12px] font-semibold text-[#1d1d1f] uppercase tracking-wide mb-1.5",
  input: "w-full px-3.5 py-2.5 rounded-xl border border-[#d2d2d7]/80 focus:border-[#8b1e22] focus:ring-2 focus:ring-[#8b1e22]/20 outline-none text-[14px] font-medium text-[#1d1d1f] placeholder:text-[#86868b] bg-[#f5f5f7] focus:bg-white transition-all font-sans",
};

const INITIAL_FORM_DATA: ForumFormData = {
  childName: "",
  school: "American School",
  age: "",
  housePhone: "",
  motherName: "",
  motherCell: "",
  fatherName: "",
  fatherCell: "",
  parentEmail: "",
  emergencyContacts: [{ name: "", relation: "", phone: "" }],
  medicalConditions: "",
  selectedClasses: [],
  fiscalName: "",
  documentType: "RNC",
  documentNumber: "",
  comprobanteType: "B02",
  schoolType: "EXTERNO",
  studentCode: "",
  paymentFrequency: "BIMESTRAL",
  cardCode: "",
  classFrequencies: {},
};

const Forum: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [lang, setLang] = useState<ForumLang>(() => {
    try {
      const stored = localStorage.getItem(LANG_STORAGE_KEY);
      if (stored === "es" || stored === "en") return stored;
    } catch (_) {}
    return "es";
  });
  const [languageChanging, setLanguageChanging] = useState(false);
  const [languageChangingTo, setLanguageChangingTo] = useState<ForumLang | null>(null);
  const [policyModal, setPolicyModal] = useState<"terms" | "refunds" | "privacy" | null>(null);
  const [logoError, setLogoError] = useState(false);
  const [forumClasses, setForumClasses] = useState<ForumClassFromApi[]>([]);
  const [classesLoading, setClassesLoading] = useState(true);
  const [classesError, setClassesError] = useState<string | null>(null);
  const [activeRegistrationMonth, setActiveRegistrationMonth] = useState<string | null>(null); // YYYY-MM
  const [currentStep, setCurrentStep] = useState<Step>(Step.CHILD_INFO);
  const [includeMother, setIncludeMother] = useState(true);
  const [includeFather, setIncludeFather] = useState(true);
  const [paymentMethod, setPaymentMethod] = useState<"ONLINE" | "LOCAL" | null>(null);
  /** null = not answered yet; true = wants fiscal receipt (show fiscal fields); false = no receipt (fiscal optional) */
  const [wantsComprobanteFiscal, setWantsComprobanteFiscal] = useState<boolean | null>(null);
  /** When renewal: we pre-filled selectedClasses from current enrollments */
  const [renewalClassesPreFilled, setRenewalClassesPreFilled] = useState(false);
  /** Class IDs the student is already enrolled in (renewal); used to show "Already enrolled" badge */
  const [currentEnrolledClassIds, setCurrentEnrolledClassIds] = useState<string[]>([]);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [duplicateStudentCodeOnChildStep, setDuplicateStudentCodeOnChildStep] = useState(false);
  const [checkingStudentCode, setCheckingStudentCode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [_submittedRegistrationId, setSubmittedRegistrationId] = useState<string | null>(null);
  const [stepValidationError, setStepValidationError] = useState<string | null>(null);
  /** When user comes back from payment page (?restore=id), we pre-fill form and skip re-submit on Pay */
  const [restoredRegistrationId, setRestoredRegistrationId] = useState<string | null>(null);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [restoreError, setRestoreError] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const [adminSettings, setAdminSettings] = useState<AdminSettings | null>(null);
  const [studentSearchResults, setStudentSearchResults] = useState<{ cardName: string; cardCode: string }[]>([]);
  const [studentSearchOpen, setStudentSearchOpen] = useState(false);
  const [studentSearchLoading, setStudentSearchLoading] = useState(false);
  const studentSearchRef = useRef<HTMLDivElement>(null);
  const [frequencyModal, setFrequencyModal] = useState<{
    classId: string;
    name: string;
    monthlyPrice: number;
    pendingFreq: "BIMESTRAL" | "CUATRIMESTRAL";
  } | null>(null);

  // Parent portal pre-fill: set when navigating from ParentDashboard.
  const [prefillFromPortal, setPrefillFromPortal] = useState(false);
  const [blockedClassPeriods, setBlockedClassPeriods] = useState<
    Array<{ classId: string; reEnrollMonth: string }>
  >([]);

  const changeLanguage = useCallback((newLang: ForumLang) => {
    if (newLang === lang) return;
    setLanguageChangingTo(newLang);
    setLanguageChanging(true);
    setTimeout(() => {
      setLang(newLang);
      try {
        localStorage.setItem(LANG_STORAGE_KEY, newLang);
      } catch (_) {}
      setLanguageChanging(false);
      setLanguageChangingTo(null);
    }, LANGUAGE_CHANGE_MS);
  }, [lang]);

  useEffect(() => {
    let cancelled = false;
    setClassesLoading(true);
    setClassesError(null);

    const loadClasses = getClassesForForum();
    const loadMonth = getActiveRegistrationMonth();
    const loadSettings = getAdminSettings();

    Promise.allSettled([loadClasses, loadMonth, loadSettings]).then(([classesResult, monthResult, settingsResult]) => {
      if (cancelled) return;
      if (classesResult.status === "fulfilled") {
        setForumClasses(classesResult.value.classes);
      } else {
        setClassesError(classesResult.reason instanceof Error ? classesResult.reason.message : t(lang, "failedToLoadClasses"));
      }
      if (monthResult.status === "fulfilled") {
        setActiveRegistrationMonth(monthResult.value.month);
      }
      if (settingsResult.status === "fulfilled") {
        setAdminSettings(settingsResult.value.settings);
      }
    }).finally(() => {
      if (!cancelled) setClassesLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [lang]);

  const restoreId = searchParams.get("restore");
  const isRestoring = Boolean(restoreId) && restoreLoading;
  useEffect(() => {
    if (!restoreId || restoredRegistrationId) return;
    setRestoreLoading(true);
    setRestoreError(null);
    getRegistrationForRestore(restoreId)
      .then((res) => {
        setFormData((prev) => ({
          ...prev,
          childName: res.child.childName ?? "",
          school: res.child.school ?? "",
          age: res.child.age ?? "",
          housePhone: res.parents.housePhone ?? "",
          motherName: res.parents.motherName ?? "",
          motherCell: res.parents.motherCell ?? "",
          fatherName: res.parents.fatherName ?? "",
          fatherCell: res.parents.fatherCell ?? "",
          parentEmail: res.parents.parentEmail ?? "",
          emergencyContacts:
            res.emergencyContacts?.length > 0
              ? res.emergencyContacts
              : [{ name: "", relation: "", phone: "" }],
          medicalConditions: res.medicalConditions ?? "",
          selectedClasses: res.selectedClasses ?? [],
          fiscalName: res.fiscalData?.fiscalName ?? prev.fiscalName,
          documentType: res.fiscalData?.documentType ?? prev.documentType,
          documentNumber: res.fiscalData?.documentNumber ?? prev.documentNumber,
          comprobanteType: (() => {
            const t = res.fiscalData?.comprobanteType ?? prev.comprobanteType;
            if (t === "B01" || t === "B14" || t === "B15") return t;
            if (String(t) === "CREDITO_FISCAL") return "B01";
            return "B02";
          })(),
          schoolType: res.schoolType ?? prev.schoolType,
          studentCode: res.studentCode ?? prev.studentCode,
          paymentFrequency: res.paymentFrequency ?? prev.paymentFrequency,
        }));
        setIncludeMother(!!(res.parents.motherName || res.parents.motherCell));
        setIncludeFather(!!(res.parents.fatherName || res.parents.fatherCell));
        setPaymentMethod(res.paymentMethod);
        setRestoredRegistrationId(res.registrationId);
        const showSuccess = searchParams.get("success") === "1";
        if (showSuccess) {
          setSubmittedRegistrationId(res.registrationId);
          setCurrentStep(Step.SUCCESS);
        } else {
          setCurrentStep(Step.REVIEW_PAY);
        }
      })
      .catch((err) => setRestoreError(err instanceof Error ? err.message : t(lang, "restoreFailed")))
      .finally(() => setRestoreLoading(false));
  }, [restoreId, restoredRegistrationId, searchParams]);

  const contactEmail = adminSettings?.supportEmail?.trim() || "info@americanschool.edu.do";
  const contactPhone = adminSettings?.supportPhone?.trim() || "(809) 567-6824";
  const schoolAddress = adminSettings?.schoolAddress?.trim() || "Santo Domingo, República Dominicana";

  const [formData, setFormData] = useState<ForumFormData>(INITIAL_FORM_DATA);

  // Apply parent portal pre-fill on mount when navigating from ParentDashboard.
  useEffect(() => {
    const state = location.state as {
      prefillCardCode?: string;
      prefillStudentName?: string;
      blockedClasses?: Array<{ classId: string; reEnrollMonth: string }>;
    } | null;
    if (!state?.prefillCardCode) return;
    setFormData((prev) => ({
      ...prev,
      cardCode: state.prefillCardCode!,
      studentCode: state.prefillCardCode!,
      ...(state.prefillStudentName ? { childName: state.prefillStudentName } : {}),
    }));
    setPrefillFromPortal(true);
    if (state.blockedClasses?.length) {
      setBlockedClassPeriods(state.blockedClasses);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset renewal pre-fill when user changes institutional code so we can re-fetch if they return to class selection
  const lastStudentCodeRef = React.useRef(formData.studentCode);
  useEffect(() => {
    if (lastStudentCodeRef.current !== formData.studentCode) {
      lastStudentCodeRef.current = formData.studentCode;
      setRenewalClassesPreFilled(false);
      setCurrentEnrolledClassIds([]);
    }
  }, [formData.studentCode]);

  // Renewal: when entering class selection with institutional code, pre-fill current classes so they can add/change
  useEffect(() => {
    if (
      currentStep !== Step.CLASS_SELECTION ||
      formData.school !== "American School" ||
      !formData.studentCode.trim() ||
      renewalClassesPreFilled
    ) {
      return;
    }
    setRenewalClassesPreFilled(true);
    getCurrentClassesByStudentCode(formData.studentCode.trim())
      .then(({ classIds }) => {
        setCurrentEnrolledClassIds(classIds);
        if (classIds.length > 0) {
          setFormData((prev) => ({
            ...prev,
            selectedClasses: [...new Set([...classIds, ...prev.selectedClasses])],
          }));
        }
      })
      .catch(() => setCurrentEnrolledClassIds([]));
  }, [currentStep, formData.school, formData.studentCode, renewalClassesPreFilled]);

  const formatPhoneNumber = (value: string) => {
    const numbers = value.replace(/\D/g, "").substring(0, 10);
    const char: Record<number, string> = { 3: " - ", 6: " - " };
    let res = "";
    for (let i = 0; i < numbers.length; i++) {
      res += (char[i] || "") + numbers[i];
    }
    return res;
  };

  const handlePhoneChange = (
    field: keyof ForumFormData | { contactIdx: number; field: keyof EmergencyContact },
    value: string
  ) => {
    const formatted = formatPhoneNumber(value);
    if (typeof field === "string") {
      setFormData((prev) => ({ ...prev, [field]: formatted }));
    } else {
      const newContacts = [...formData.emergencyContacts];
      newContacts[field.contactIdx] = { ...newContacts[field.contactIdx], [field.field]: formatted };
      setFormData((prev) => ({ ...prev, emergencyContacts: newContacts }));
    }
  };

  const updateFormData = (fields: Partial<ForumFormData>) => {
    setFormData((prev) => ({ ...prev, ...fields }));
    setStepValidationError(null);
  };

  useEffect(() => {
    const trimmed = formData.childName.trim();
    console.log("[studentSearch] effect fired — childName:", JSON.stringify(formData.childName), "trimmed:", JSON.stringify(trimmed));
    if (trimmed.length < 3) {
      console.log("[studentSearch] input too short (<3 chars), skipping query");
      setStudentSearchResults([]);
      setStudentSearchOpen(false);
      return;
    }
    const timer = setTimeout(async () => {
      console.log("[studentSearch] debounce fired — db:", !!db);
      if (!db) return;
      setStudentSearchLoading(true);
      setStudentSearchOpen(true);
      try {
        const lower = trimmed.toLowerCase();
        console.log("[studentSearch] querying cardNameTokens array-contains", JSON.stringify(lower));
        const q = query(
          collection(db, "students"),
          where("cardNameTokens", "array-contains", lower)
        );
        const snap = await getDocs(q);
        console.log("[studentSearch] results:", snap.size, snap.docs.map((d) => d.data().cardName));
        setStudentSearchResults(
          snap.docs.map((doc) => ({
            cardName: doc.data().cardName as string,
            cardCode: (doc.data().cardCode ?? "") as string,
          }))
        );
      } catch (err) {
        console.error("[studentSearch] query error:", err);
        setStudentSearchResults([]);
      } finally {
        setStudentSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [formData.childName]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (studentSearchRef.current && !studentSearchRef.current.contains(e.target as Node)) {
        setStudentSearchOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /** Returns an error message if the current step's required fields are incomplete; null if OK to proceed. */
  const getStepValidationError = (step: Step): string | null => {
    switch (step) {
      case Step.CHILD_INFO:
        if (!formData.childName.trim()) return t(lang, "validationChildName");
        if (!formData.school.trim()) return t(lang, "validationSchool");
        if (!formData.age) return t(lang, "validationAge");
        return null;
      case Step.PARENT_INFO:
        if (!formData.parentEmail.trim()) return t(lang, "validationParentEmail");
        if (includeMother && !formData.motherName.trim() && !formData.motherCell.replace(/\D/g, "").trim()) {
          return t(lang, "validationMother");
        }
        if (includeFather && !formData.fatherName.trim() && !formData.fatherCell.replace(/\D/g, "").trim()) {
          return t(lang, "validationFather");
        }
        return null;
      case Step.SAFETY_INFO: {
        const hasValidContact = formData.emergencyContacts.some(
          (c) => c.name.trim() && c.phone.replace(/\D/g, "").length >= 10
        );
        if (!hasValidContact) return t(lang, "validationEmergency");
        return null;
      }
      case Step.CLASS_SELECTION:
        if (formData.selectedClasses.length === 0) return t(lang, "validationClass");
        return null;
      case Step.REVIEW_PAY:
        if (wantsComprobanteFiscal === null) {
          return lang === "es" ? "Indique si desea comprobante fiscal." : "Please indicate whether you want a fiscal receipt.";
        }
        if (wantsComprobanteFiscal === true) {
          if (!formData.fiscalName.trim()) return "Complete el nombre fiscal.";
          if (formData.comprobanteType === "B01" && !formData.documentNumber.trim()) return "Crédito Fiscal (B01) requiere número de documento.";
        }
        return null;
      default:
        return null;
    }
  };

  const handleNext = async () => {
    const err = getStepValidationError(currentStep);
    if (err) {
      setStepValidationError(err);
      return;
    }
    setStepValidationError(null);
    setDuplicateStudentCodeOnChildStep(false);

    if (currentStep === Step.CHILD_INFO && formData.school === "American School" && formData.studentCode.trim() && !prefillFromPortal) {
      setCheckingStudentCode(true);
      try {
        const res = await checkStudentCodeAvailable(formData.studentCode.trim());
        if (!res.available) {
          setDuplicateStudentCodeOnChildStep(true);
          setCheckingStudentCode(false);
          return;
        }
      } catch (_) {
        setCheckingStudentCode(false);
        return;
      }
      setCheckingStudentCode(false);
    }

    if (currentStep === Step.PARENT_INFO) {
      const contacts: EmergencyContact[] = [];
      if (includeMother && (formData.motherName || formData.motherCell)) {
        contacts.push({
          name: formData.motherName,
          relation: "Madre / Mother",
          phone: formData.motherCell,
        });
      }
      if (includeFather && (formData.fatherName || formData.fatherCell)) {
        contacts.push({
          name: formData.fatherName,
          relation: "Padre / Father",
          phone: formData.fatherCell,
        });
      }
      if (
        formData.emergencyContacts.length <= 1 &&
        !formData.emergencyContacts[0].name
      ) {
        updateFormData({
          emergencyContacts:
            contacts.length > 0 ? contacts : [{ name: "", relation: "", phone: "" }],
        });
      }
    }
    setSubmitError(null);
    setCurrentStep((prev) => prev + 1);
  };

  const handlePrev = () => {
    setSubmitError(null);
    setStepValidationError(null);
    setCurrentStep((prev) => prev - 1);
  };

  const selectedClassDetails = useMemo(
    () => forumClasses.filter((c) => formData.selectedClasses.includes(c.id)),
    [forumClasses, formData.selectedClasses]
  );

  const isAnySelectedClassFull = useMemo(() => {
    return selectedClassDetails.some((c) => {
      const cap = c.maxCapacity ?? c.cupos;
      return cap != null && cap > 0 && (c.currentEnrollment ?? 0) >= cap;
    });
  }, [selectedClassDetails]);

  /** Classes sorted alphabetically by display name (current language) */
  const sortedForumClasses = useMemo(() => {
    return [...forumClasses].sort((a, b) => {
      const nameA = (lang === "es" ? (a.nameEs ?? a.name) : (a.nameEn ?? a.name)) ?? "";
      const nameB = (lang === "es" ? (b.nameEs ?? b.name) : (b.nameEn ?? b.name)) ?? "";
      return nameA.localeCompare(nameB, undefined, { sensitivity: "base" });
    });
  }, [forumClasses, lang]);

  const totalPrice = useMemo(() => {
    return selectedClassDetails.reduce((sum, cls) => {
      const freq = formData.classFrequencies[cls.id] ?? "BIMESTRAL";
      const months = freq === "CUATRIMESTRAL" ? 4 : 2;
      const discount = freq === "CUATRIMESTRAL" ? 0.95 : 1.0;
      return sum + Math.round(cls.monthlyPrice * months * discount);
    }, 0);
  }, [selectedClassDetails, formData.classFrequencies]);

  const toggleClass = (classId: string) => {
    setFormData((prev) => {
      const isSelected = prev.selectedClasses.includes(classId);
      if (isSelected) {
        const newFrequencies = { ...prev.classFrequencies };
        delete newFrequencies[classId];
        return {
          ...prev,
          selectedClasses: prev.selectedClasses.filter((id) => id !== classId),
          classFrequencies: newFrequencies,
        };
      }
      return { ...prev, selectedClasses: [...prev.selectedClasses, classId] };
    });
  };

  const confirmFrequency = () => {
    if (!frequencyModal) return;
    setFormData((prev) => ({
      ...prev,
      selectedClasses: [...prev.selectedClasses, frequencyModal.classId],
      classFrequencies: { ...prev.classFrequencies, [frequencyModal.classId]: frequencyModal.pendingFreq },
    }));
    setFrequencyModal(null);
  };

  const addEmergencyContact = () => {
    if (formData.emergencyContacts.length < 4) {
      updateFormData({
        emergencyContacts: [
          ...formData.emergencyContacts,
          { name: "", relation: "", phone: "" },
        ],
      });
    }
  };

  const removeEmergencyContact = (index: number) => {
    const newContacts = formData.emergencyContacts.filter((_, i) => i !== index);
    updateFormData({
      emergencyContacts:
        newContacts.length > 0 ? newContacts : [{ name: "", relation: "", phone: "" }],
    });
  };

  const handleSubmitEnrollment = async () => {
    if (!paymentMethod) return;
    if (isAnySelectedClassFull) {
      setSubmitError("Este curso ha alcanzado el límite de cupos disponibles.");
      return;
    }
    // When user came back from payment page (restore), just go to payment again — do not re-submit
    if (restoredRegistrationId && paymentMethod === "ONLINE") {
      navigate(`/pay/by-id/${encodeURIComponent(restoredRegistrationId)}`, { replace: true });
      return;
    }
    // Require all form steps to be complete before allowing payment
    for (let s = Step.CHILD_INFO; s <= Step.CLASS_SELECTION; s++) {
      const err = getStepValidationError(s);
      if (err) {
        setSubmitError(t(lang, "completeAllFields"));
        return;
      }
    }
    const reviewErr = getStepValidationError(Step.REVIEW_PAY);
    if (reviewErr) {
      setSubmitError(reviewErr);
      return;
    }
    setSubmitError(null);
    setIsSubmitting(true);
    try {
      const payload: SubmitRegistrationRequest = {
        child: {
          childName: formData.childName.trim(),
          school: formData.school.trim(),
          age: formData.age,
        },
        parents: {
          housePhone: formData.housePhone,
          motherName: formData.motherName,
          motherCell: formData.motherCell,
          fatherName: formData.fatherName,
          fatherCell: formData.fatherCell,
          parentEmail: formData.parentEmail.trim(),
        },
        emergencyContacts: formData.emergencyContacts.filter(
          (c) => c.name.trim() || c.relation.trim() || c.phone.trim()
        ),
        medicalConditions: formData.medicalConditions.trim(),
        selectedClasses: formData.selectedClasses,
        totalPrice,
        paymentMethod,
        schoolType: formData.school === "American School" ? "AMERICAN_SCHOOL" : "EXTERNO",
        studentCode: formData.school === "American School" ? formData.studentCode.trim() : undefined,
        paymentFrequency: formData.paymentFrequency,
        ...(formData.cardCode.trim() && { cardCode: formData.cardCode.trim() }),
      };
      // Only include fiscalData when user chose "Sí, deseo comprobante fiscal"
      if (wantsComprobanteFiscal === true) {
        payload.fiscalData = {
          fiscalName: formData.fiscalName.trim(),
          documentType: formData.documentType,
          documentNumber: formData.documentNumber.trim(),
          comprobanteType: "B01", // always Crédito Fiscal when user requests comprobante
          requireNCF: true,
        };
      }
      // Obtain a reCAPTCHA v3 token. If reCAPTCHA is not configured (site key
      // missing), the provider is not mounted and executeRecaptcha is undefined —
      // in that case we submit without a token (backend treats missing token as
      // "unverified" and logs a warning rather than hard-blocking, so dev/staging
      // flows work without the key set).
      let recaptchaToken: string | undefined;
      if (executeRecaptcha) {
        try {
          recaptchaToken = await executeRecaptcha("SUBMIT_REGISTRATION");
        } catch {
          // Non-fatal: log and continue. Score check is enforced server-side.
          console.warn("reCAPTCHA token generation failed");
        }
      }
      if (recaptchaToken) {
        (payload as unknown as Record<string, unknown>).recaptchaToken = recaptchaToken;
      }

      const res = await submitRegistration(payload);
      const regId = res.registrationId ?? null;
      setSubmittedRegistrationId(regId);
      if (paymentMethod === "ONLINE" && regId) {
        navigate(`/pay/by-id/${encodeURIComponent(regId)}`, { replace: true });
        return;
      }
      setCurrentStep(Step.SUCCESS);
    } catch (err) {
      const message = err instanceof Error ? err.message : t(lang, "registrationFailed");
      const isDuplicateCode = String(message).includes("DUPLICATE_STUDENT_CODE") || String(message).includes("Ya existe un estudiante con este código");
      setSubmitError(isDuplicateCode ? "DUPLICATE_STUDENT_CODE" : message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isRestoring) {
    return (
      <div className="min-h-screen flex flex-col bg-[#f5f5f7] items-center justify-center p-6">
        <div className="text-center">
          <div
            className="animate-spin rounded-full h-10 w-10 border-2 border-[#d2d2d7] border-t-[#161b3d] mx-auto"
            aria-hidden
          />
          <p className="mt-4 text-[14px] font-medium text-[#86868b]">
            {t(lang, "loadingForm")}
          </p>
        </div>
      </div>
    );
  }

  if (languageChanging && languageChangingTo) {
    return (
      <div className="min-h-screen flex flex-col bg-[#f5f5f7] items-center justify-center p-6">
        <div className="text-center">
          <div
            className="animate-spin rounded-full h-10 w-10 border-2 border-[#d2d2d7] border-t-[#161b3d] mx-auto"
            aria-hidden
          />
          <p className="mt-4 text-[14px] font-medium text-[#86868b]">
            {t(languageChangingTo, "changingLanguage")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#f8fafc] overflow-x-hidden">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 h-24 flex items-center justify-between">
          <div className="flex items-center space-x-5">
            <div className="w-16 h-16 flex items-center justify-center overflow-hidden">
              {logoError ? (
                <div
                  className="w-16 h-16 rounded-xl flex items-center justify-center text-white font-bold text-2xl shadow-lg border-2 border-white"
                  style={{ backgroundColor: BRAND.navy }}
                >
                  AS
                </div>
              ) : (
                <img
                  src="/logo/logo.png"
                  alt="American School"
                  className="w-full h-full object-contain"
                  onError={() => setLogoError(true)}
                />
              )}
            </div>
            <div className="border-l border-gray-100 pl-5 py-1">
              <h1
                className="text-xl font-black tracking-tight uppercase leading-none"
                style={{ color: BRAND.navy }}
              >
                {t(lang, "schoolTitle")}
              </h1>
              <p
                className="text-xs font-bold mt-1 tracking-wider uppercase"
                style={{ color: BRAND.maroon }}
              >
                {t(lang, "registrationSubtitle")}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 md:gap-4">
            {currentStep !== Step.SUCCESS && (
              <div className="hidden md:flex items-center space-x-3 text-[11px] font-semibold uppercase tracking-wide text-[#86868b]">
                <span
                  className={
                    currentStep === Step.CHILD_INFO
                      ? "text-[#8b1e22]"
                      : currentStep > Step.CHILD_INFO
                        ? "text-[#161b3d]"
                        : ""
                  }
                >
                  {t(lang, "stepStudent")}
                </span>
                <span>&bull;</span>
                <span
                  className={
                    currentStep === Step.PARENT_INFO
                      ? "text-[#8b1e22]"
                      : currentStep > Step.PARENT_INFO
                        ? "text-[#161b3d]"
                        : ""
                  }
                >
                  {t(lang, "stepFamily")}
                </span>
                <span>&bull;</span>
                <span
                  className={
                    currentStep === Step.SAFETY_INFO
                      ? "text-[#8b1e22]"
                      : currentStep > Step.SAFETY_INFO
                        ? "text-[#161b3d]"
                        : ""
                  }
                >
                  {t(lang, "stepSafety")}
                </span>
                <span>&bull;</span>
                <span
                  className={
                    currentStep === Step.CLASS_SELECTION || currentStep === Step.REVIEW_PAY
                      ? "text-[#8b1e22]"
                      : ""
                  }
                >
                  {t(lang, "stepPayment")}
                </span>
              </div>
            )}
            {/* Language: top right, next to Pago — small box, lighter blue */}
            <div className="flex rounded border border-gray-200/80 bg-gray-50/60 p-px" role="group" aria-label={lang === "es" ? "Idioma" : "Language"}>
              <button
                type="button"
                onClick={() => changeLanguage("es")}
                className={`rounded-sm px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider transition-colors ${
                  lang === "es"
                    ? "text-white"
                    : "text-gray-500 hover:bg-gray-100/80 hover:text-gray-700"
                }`}
                style={lang === "es" ? { backgroundColor: BRAND.navyLight } : undefined}
              >
                ES
              </button>
              <button
                type="button"
                onClick={() => changeLanguage("en")}
                className={`rounded-sm px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider transition-colors ${
                  lang === "en"
                    ? "text-white"
                    : "text-gray-500 hover:bg-gray-100/80 hover:text-gray-700"
                }`}
                style={lang === "en" ? { backgroundColor: BRAND.navyLight } : undefined}
              >
                EN
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Already submitted? Redirect to parent portal login (requires email + password). */}
      {/* The matrícula-only public lookup was removed: anyone with an ID could see payment data. */}

      <main className="flex-1 max-w-5xl mx-auto w-full px-4 py-6">
        {restoreError && (
          <div className="mb-4 rounded-xl bg-red-50 border border-red-100 px-4 py-3 text-[14px] text-red-700">
            {restoreError}
            <Link to="/" className="ml-2 font-semibold underline">{t(lang, "backToHome")}</Link>
          </div>
        )}
        <div className="bg-white rounded-[2rem] shadow-2xl shadow-gray-200/50 border border-gray-100 overflow-hidden">
          {currentStep !== Step.SUCCESS && (
            <div className="h-1.5 bg-gray-100 w-full flex">
              <div
                className="h-full transition-all duration-700 ease-in-out"
                style={{
                  width: `${(currentStep / 5) * 100}%`,
                  backgroundColor: BRAND.maroon,
                }}
              />
            </div>
          )}

          <div className="p-6 md:p-10">
            {currentStep === Step.CHILD_INFO && (
              <section className="animate-fadeIn font-sans">
                <div className="mb-6">
                  <h2 className="text-2xl font-black text-gray-900 mb-1">{t(lang, "studentInfo")}</h2>
                </div>
                <div className="space-y-6">
                  <div className="relative group" ref={studentSearchRef}>
                    <label className={`${FORUM.label} group-focus-within:text-[#8b1e22] transition-colors`}>
                      {t(lang, "fullName")}
                    </label>
                    <input
                      type="text"
                      className={FORUM.input}
                      placeholder={t(lang, "fullNamePlaceholder")}
                      value={formData.childName}
                      autoComplete="off"
                      onChange={(e) => updateFormData({ childName: e.target.value, cardCode: "" })}
                      onFocus={() => {
                        if (studentSearchResults.length > 0) setStudentSearchOpen(true);
                      }}
                    />
                    {studentSearchOpen && (
                      <div className="absolute z-50 top-full mt-1 w-full rounded-xl border border-[#d2d2d7]/80 bg-white shadow-lg overflow-hidden">
                        {studentSearchLoading ? (
                          <div className="px-4 py-3 text-[13px] text-[#86868b]">
                            {lang === "es" ? "Buscando..." : "Searching..."}
                          </div>
                        ) : studentSearchResults.length === 0 ? (
                          <div className="px-4 py-3 text-[13px] text-[#86868b]">
                            {t(lang, "studentSearchNoResults")}
                          </div>
                        ) : (
                          <ul>
                            {studentSearchResults.map((r, i) => (
                              <li
                                key={i}
                                className="px-4 py-2.5 text-[14px] font-medium text-[#1d1d1f] hover:bg-[#f5f5f7] cursor-pointer transition-colors"
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  updateFormData({ childName: r.cardName, cardCode: r.cardCode });
                                  setStudentSearchOpen(false);
                                }}
                              >
                                {r.cardName}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="group">
                      <label className={`${FORUM.label} group-focus-within:text-[#8b1e22] transition-colors`}>
                        {t(lang, "school")}
                      </label>
                      <select
                        className={FORUM.input}
                        value={formData.school === "American School" ? "" : "other"}
                        onChange={(e) => {
                          const isAmerican = e.target.value !== "other";
                          setDuplicateStudentCodeOnChildStep(false);
                          updateFormData({
                            school: isAmerican ? "American School" : "",
                            ...(isAmerican ? {} : { studentCode: "" }),
                          });
                        }}
                      >
                        <option value="">American School</option>
                        <option value="other">{lang === "es" ? "Otra" : "Other"}</option>
                      </select>
                      {formData.school !== "American School" && (
                        <input
                          type="text"
                          className={`${FORUM.input} mt-2`}
                          placeholder={lang === "es" ? "Escribe el nombre de la escuela" : "Enter school name"}
                          value={formData.school}
                          onChange={(e) => updateFormData({ school: e.target.value })}
                        />
                      )}
                    </div>
                    <div className="group">
                      <label className={`${FORUM.label} group-focus-within:text-[#8b1e22] transition-colors`}>
                        {t(lang, "age")}
                      </label>
                      <select
                        className={FORUM.input}
                        value={formData.age}
                        onChange={(e) => updateFormData({ age: e.target.value })}
                      >
                        <option value="">{t(lang, "selectAge")}</option>
                        {SCHOOL_AGES.map((age) => (
                          <option key={age} value={age}>
                            {tfn(lang, "ageYears", age)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>


                  {duplicateStudentCodeOnChildStep && (
                    <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50/90 px-5 py-4 shadow-sm" role="alert">
                      <div className="flex gap-3">
                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center" aria-hidden>
                          <svg className="w-5 h-5 text-amber-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[14px] font-semibold text-amber-900">
                            {t(lang, "duplicateStudentCodeTitle")}
                          </p>
                          <p className="mt-1.5 text-[13px] text-amber-800 leading-snug">
                            {t(lang, "duplicateStudentCodeMessage")}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </section>
            )}

            {currentStep === Step.PARENT_INFO && (
              <section className="animate-fadeIn font-sans">
                <div className="mb-6">
                  <h2 className="text-2xl font-black text-gray-900 mb-1">{t(lang, "familyDetails")}</h2>
                </div>
                <div className="space-y-6">
                  <div className="md:col-span-2 group">
                    <label className={`${FORUM.label} group-focus-within:text-[#8b1e22] transition-colors`}>
                      {t(lang, "housePhone")}
                    </label>
                    <input
                      type="tel"
                      className={FORUM.input}
                      placeholder={t(lang, "housePhonePlaceholder")}
                      value={formData.housePhone}
                      onChange={(e) =>
                        handlePhoneChange("housePhone", e.target.value)
                      }
                    />
                  </div>

                  {includeMother ? (
                    <div className="bg-gray-50/50 p-4 rounded-xl border border-gray-100 relative group animate-fadeIn">
                      <button
                        onClick={() => setIncludeMother(false)}
                        className="absolute -top-2 -right-2 w-7 h-7 bg-white border-2 rounded-full flex items-center justify-center shadow-sm transition-all z-10 text-[#8b1e22] border-[#8b1e22] hover:text-red-500 hover:border-red-100"
                      >
                        <svg
                          className="w-3.5 h-3.5"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                      <p className={`${FORUM.label} mb-2`} style={{ color: BRAND.maroon }}>
                        {t(lang, "motherContact")}
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <input
                          className={FORUM.input}
                          placeholder={t(lang, "fullNamePlaceholderShort")}
                          value={formData.motherName}
                          onChange={(e) =>
                            updateFormData({ motherName: e.target.value })
                          }
                        />
                        <input
                          className={FORUM.input}
                          placeholder={t(lang, "cellPhone")}
                          value={formData.motherCell}
                          onChange={(e) =>
                            handlePhoneChange("motherCell", e.target.value)
                          }
                        />
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => setIncludeMother(true)}
                      className="w-full py-4 border-2 border-dashed border-gray-100 rounded-3xl text-gray-400 font-bold text-xs uppercase tracking-widest hover:border-[#161b3d]/30 hover:text-[#161b3d] transition-all"
                    >
                      {t(lang, "addMother")}
                    </button>
                  )}

                  {includeFather ? (
                    <div className="bg-gray-50/50 p-4 rounded-xl border border-gray-100 relative group animate-fadeIn">
                      <button
                        onClick={() => setIncludeFather(false)}
                        className="absolute -top-2 -right-2 w-7 h-7 bg-white border-2 rounded-full flex items-center justify-center shadow-sm transition-all z-10 text-[#8b1e22] border-[#8b1e22] hover:text-red-500 hover:border-red-100"
                      >
                        <svg
                          className="w-3.5 h-3.5"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                      <p className={`${FORUM.label} mb-2`} style={{ color: BRAND.maroon }}>
                        {t(lang, "fatherContact")}
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <input
                          className={FORUM.input}
                          placeholder={t(lang, "fullNamePlaceholderShort")}
                          value={formData.fatherName}
                          onChange={(e) =>
                            updateFormData({ fatherName: e.target.value })
                          }
                        />
                        <input
                          className={FORUM.input}
                          placeholder={t(lang, "cellPhone")}
                          value={formData.fatherCell}
                          onChange={(e) =>
                            handlePhoneChange("fatherCell", e.target.value)
                          }
                        />
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => setIncludeFather(true)}
                      className="w-full py-4 border-2 border-dashed border-gray-100 rounded-3xl text-gray-400 font-bold text-xs uppercase tracking-widest hover:border-[#161b3d]/30 hover:text-[#161b3d] transition-all"
                    >
                      {t(lang, "addFather")}
                    </button>
                  )}

                  <div className="group">
                    <label className={`${FORUM.label} group-focus-within:text-[#8b1e22] transition-colors`}>
                      {t(lang, "parentEmail")}
                    </label>
                    <input
                      type="email"
                      className={FORUM.input}
                      value={formData.parentEmail}
                      onChange={(e) =>
                        updateFormData({ parentEmail: e.target.value })
                      }
                    />
                  </div>
                </div>
              </section>
            )}

            {currentStep === Step.SAFETY_INFO && (
              <section className="animate-fadeIn font-sans">
                <div className="mb-6 flex justify-between items-end">
                  <div>
                    <h2 className="text-2xl font-black text-gray-900 mb-1">{t(lang, "emergencyMedical")}</h2>
                  </div>
                  {formData.emergencyContacts.length < 4 && (
                    <button
                      onClick={addEmergencyContact}
                      className="px-3.5 py-2 bg-[#161b3d] text-white text-[12px] font-semibold uppercase tracking-wide rounded-xl hover:bg-[#8b1e22] transition-colors"
                    >
                      {t(lang, "addContact")}
                    </button>
                  )}
                </div>
                <div className="space-y-8">
                  {formData.emergencyContacts.map((contact, idx) => (
                    <div
                      key={idx}
                      className="p-8 rounded-[2rem] border-2 border-red-50 bg-red-50/20 relative animate-fadeIn"
                    >
                      {formData.emergencyContacts.length > 1 && (
                        <button
                          onClick={() => removeEmergencyContact(idx)}
                          className="absolute top-4 right-4 text-red-300 hover:text-red-600 transition-colors"
                        >
                          <svg
                            className="w-5 h-5"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </button>
                      )}
                      <h3 className={`${FORUM.label} mb-4 flex items-center`} style={{ color: BRAND.maroon }}>
                        {tfn(lang, "emergencyContactN", idx + 1)}
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="space-y-2 group">
                          <label className="text-[11px] font-semibold text-red-900/50 uppercase tracking-wide group-focus-within:text-[#8b1e22] transition-colors">
                            {t(lang, "fullNamePlaceholderShort")}
                          </label>
                          <input
                            className={`${FORUM.input} border-red-100 focus:border-[#8b1e22] focus:ring-[#8b1e22]/20`}
                            placeholder={t(lang, "namePlaceholder")}
                            value={contact.name}
                            onChange={(e) => {
                              const newC = [...formData.emergencyContacts];
                              newC[idx].name = e.target.value;
                              updateFormData({ emergencyContacts: newC });
                            }}
                          />
                        </div>
                        <div className="space-y-2 group">
                          <label className="text-[11px] font-semibold text-red-900/50 uppercase tracking-wide group-focus-within:text-[#8b1e22] transition-colors">
                            {t(lang, "relationship")}
                          </label>
                          <select
                            className={`${FORUM.input} border-red-100 focus:border-[#8b1e22] focus:ring-[#8b1e22]/20 appearance-none`}
                            style={{
                              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23fca5a5'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                              backgroundRepeat: "no-repeat",
                              backgroundPosition: "right 1rem center",
                              backgroundSize: "1.2em",
                            }}
                            value={contact.relation}
                            onChange={(e) => {
                              const newC = [...formData.emergencyContacts];
                              newC[idx].relation = e.target.value;
                              updateFormData({ emergencyContacts: newC });
                            }}
                          >
                            <option value="">{t(lang, "selectRelation")}</option>
                            {RELATIONSHIPS.map((rel) => (
                              <option
                                key={rel.en}
                                value={`${rel.es} / ${rel.en}`}
                              >
                                {lang === "es" ? rel.es : rel.en}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="space-y-2 group">
                          <label className="text-[11px] font-semibold text-red-900/50 uppercase tracking-wide group-focus-within:text-[#8b1e22] transition-colors">
                            {t(lang, "phone")}
                          </label>
                          <input
                            className={`${FORUM.input} border-red-100 focus:border-[#8b1e22] focus:ring-[#8b1e22]/20`}
                            placeholder={t(lang, "housePhonePlaceholder")}
                            value={contact.phone}
                            onChange={(e) =>
                              handlePhoneChange(
                                { contactIdx: idx, field: "phone" },
                                e.target.value
                              )
                            }
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  <div className="group">
                    <label className={`${FORUM.label} group-focus-within:text-[#8b1e22] transition-colors`}>
                      {t(lang, "medicalConditionsLabel")}
                    </label>
                    <textarea
                      rows={4}
                      className={FORUM.input}
                      placeholder={t(lang, "medicalConditionsPlaceholder")}
                      value={formData.medicalConditions}
                      onChange={(e) =>
                        updateFormData({ medicalConditions: e.target.value })
                      }
                    />
                  </div>
                </div>
              </section>
            )}

            {currentStep === Step.CLASS_SELECTION && (
              <section className="animate-fadeIn font-sans">
                <div className="flex flex-row items-center justify-between mb-6 gap-4 flex-wrap">
                  <div>
                    <h2 className="text-2xl font-black text-gray-900 mb-1">{t(lang, "classSelection")}</h2>
                    {activeRegistrationMonth && (
                      <p className="text-[13px] text-[#86868b] mt-1">
                        {t(lang, "inscriptionsOpenFor")} {formatRegistrationMonthLabel(activeRegistrationMonth, lang)}
                      </p>
                    )}
                  </div>
                  <div className="bg-[#161b3d] px-6 py-4 rounded-xl flex items-center shadow-lg">
                    <div className="mr-4">
                      <p className="text-blue-300 text-[11px] font-semibold uppercase mb-0.5">
                        {t(lang, "total")}
                      </p>
                      <p className="text-white text-[17px] font-semibold">
                        RD$ {totalPrice.toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {classesLoading ? (
                    <p className="text-gray-500 col-span-2 text-sm">{t(lang, "loadingClasses")}</p>
                  ) : classesError ? (
                    <p className="text-red-600 col-span-2 text-sm">{classesError}</p>
                  ) : forumClasses.length === 0 ? (
                    <p className="text-gray-500 col-span-2 text-sm">{t(lang, "noClassesAvailable")}</p>
                  ) : (
                    sortedForumClasses.map((cls) => {
                      const hasPrice = (cls.monthlyPrice ?? 0) > 0;
                      const hasAction = cls.externalLink || cls.externalEmail;
                      const showFooter = hasPrice || hasAction;
                      const blockedPeriod = blockedClassPeriods.find((b) => b.classId === cls.id);
                      return (
                      <div
                        key={cls.id}
                        onClick={() => {
                          if (cls.externalLink || cls.externalEmail) return;
                          if (blockedPeriod) return;
                          const capacity = cls.maxCapacity ?? cls.cupos;
                          const isFull = capacity != null && capacity > 0 && (cls.currentEnrollment ?? 0) >= capacity;
                          const isSelected = formData.selectedClasses.includes(cls.id);
                          if (isFull && !isSelected) return;
                          if (isSelected) {
                            toggleClass(cls.id);
                          } else if ((cls.monthlyPrice ?? 0) > 0) {
                            const name = (lang === "es" ? (cls.nameEs ?? cls.name) : (cls.nameEn ?? cls.name)) ?? cls.name ?? "";
                            setFrequencyModal({
                              classId: cls.id,
                              name,
                              monthlyPrice: cls.monthlyPrice ?? 0,
                              pendingFreq: formData.classFrequencies[cls.id] ?? "BIMESTRAL",
                            });
                          } else {
                            toggleClass(cls.id);
                          }
                        }}
                        className={`p-4 rounded-xl border transition-all flex flex-col justify-between min-h-0 ${
                          (() => {
                            if (blockedPeriod) return "border-amber-200 bg-amber-50/40 cursor-not-allowed opacity-80";
                            const capacity = cls.maxCapacity ?? cls.cupos;
                            const isFull = capacity != null && capacity > 0 && (cls.currentEnrollment ?? 0) >= capacity;
                            const isSelected = formData.selectedClasses.includes(cls.id);
                            if (isFull && !isSelected) return "border-gray-200 bg-gray-100 cursor-not-allowed opacity-75";
                            return formData.selectedClasses.includes(cls.id)
                              ? "border-[#161b3d] bg-blue-50/30 shadow-sm cursor-pointer"
                              : "border-gray-200 hover:border-[#8b1e22]/30 bg-white cursor-pointer";
                          })()
                        }`}
                      >
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="text-[13px] font-semibold text-[#1d1d1f] tracking-tight leading-snug">
                              {lang === "es" ? (cls.nameEs ?? cls.name) : (cls.nameEn ?? cls.name)}
                            </h3>
                            {currentEnrolledClassIds.includes(cls.id) && !blockedPeriod && (
                              <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-800">
                                {lang === "es" ? "Ya inscrito" : "Already enrolled"}
                              </span>
                            )}
                            {blockedPeriod && (
                              <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-md bg-amber-100 text-amber-800">
                                {lang === "es" ? "Período activo" : "Active period"}
                              </span>
                            )}
                          </div>
                          {(() => {
                            const schedDay = lang === "en" ? (cls.scheduleDayEn ?? cls.scheduleDay) : cls.scheduleDay;
                            const fromAdmin = [schedDay, cls.scheduleTime].filter(Boolean).join(" · ");
                            const fromLegacy = [(lang === "es" ? cls.scheduleEs : cls.scheduleEn) ?? cls.scheduleEs ?? cls.scheduleEn ?? "", cls.time].filter(Boolean).join(" · ");
                            const line = fromAdmin || fromLegacy;
                            return line ? (
                              <p className="text-[11px] font-medium text-[#86868b] uppercase tracking-wide leading-tight">
                                {line}
                              </p>
                            ) : null;
                          })()}
                          {((cls.maxCapacity ?? cls.cupos) != null && (cls.maxCapacity ?? cls.cupos)! > 0) && (
                            <p className="text-[11px] font-medium text-[#1d1d1f] mt-1">
                              {(cls.currentEnrollment ?? 0)} / {(cls.maxCapacity ?? cls.cupos)!} {t(lang, "cuposOcupados")}
                            </p>
                          )}
                          {((cls.maxCapacity ?? cls.cupos) != null && (cls.maxCapacity ?? cls.cupos)! > 0 && (cls.currentEnrollment ?? 0) >= (cls.maxCapacity ?? cls.cupos)!) && (
                            <p className="text-[11px] font-semibold text-amber-700 mt-1">
                              Este curso ha alcanzado el límite de cupos disponibles.
                            </p>
                          )}
                          {blockedPeriod && (
                            <p className="text-[11px] font-semibold text-amber-700 mt-1">
                              {lang === "es"
                                ? `Disponible desde ${formatRegistrationMonthLabel(blockedPeriod.reEnrollMonth, lang)}`
                                : `Available from ${formatRegistrationMonthLabel(blockedPeriod.reEnrollMonth, lang)}`}
                            </p>
                          )}
                        </div>
                        {showFooter ? (
                          <div className="mt-3 pt-2 border-t border-[#d2d2d7]/50 flex justify-between items-center gap-2 min-h-0">
                            {hasPrice ? (
                              <span className="text-[13px] font-semibold text-[#1d1d1f]">
                                RD$ {(cls.monthlyPrice ?? 0).toLocaleString()}
                              </span>
                            ) : (
                              <span className="flex-1 min-w-0" aria-hidden />
                            )}
                            {cls.externalLink && (
                              <a
                                href={cls.externalLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="bg-[#161b3d] text-white px-2.5 py-1.5 rounded-lg text-[10px] font-semibold uppercase tracking-wide flex-shrink-0"
                              >
                                {t(lang, "link")} →
                              </a>
                            )}
                            {cls.externalEmail && (
                              <a
                                href={`mailto:${cls.externalEmail}`}
                                onClick={(e) => e.stopPropagation()}
                                className="bg-[#161b3d] text-white px-2.5 py-1.5 rounded-lg text-[10px] font-semibold uppercase tracking-wide flex-shrink-0"
                              >
                                {t(lang, "email")}
                              </a>
                            )}
                          </div>
                        ) : null}
                      </div>
                    );
                    })
                  )}
                </div>
              </section>
            )}

            {frequencyModal && (
              <div
                className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
                onClick={() => setFrequencyModal(null)}
              >
                <div
                  className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="px-6 pt-6 pb-4">
                    <p className="text-[11px] font-semibold text-[#86868b] uppercase tracking-wide mb-1">
                      {t(lang, "paymentFrequencySection")}
                    </p>
                    <h3 className="text-[17px] font-bold text-[#1d1d1f] tracking-tight leading-snug">
                      {frequencyModal.name}
                    </h3>
                  </div>
                  <div className="px-6 pb-4 space-y-3">
                    {(["BIMESTRAL", "CUATRIMESTRAL"] as const).map((freq) => {
                      const months = freq === "CUATRIMESTRAL" ? 4 : 2;
                      const discount = freq === "CUATRIMESTRAL" ? 0.95 : 1.0;
                      const price = Math.round(frequencyModal.monthlyPrice * months * discount);
                      const isActive = frequencyModal.pendingFreq === freq;
                      return (
                        <button
                          key={freq}
                          type="button"
                          onClick={() => setFrequencyModal((m) => m ? { ...m, pendingFreq: freq } : null)}
                          className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                            isActive
                              ? "border-[#161b3d] bg-[#161b3d]/5"
                              : "border-[#d2d2d7] hover:border-[#161b3d]/30 bg-white"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className={`text-[14px] font-semibold ${isActive ? "text-[#161b3d]" : "text-[#1d1d1f]"}`}>
                                {freq === "BIMESTRAL" ? t(lang, "freqBimestral") : t(lang, "freqCuatrimestral")}
                              </p>
                              <p className="text-[12px] text-[#86868b] mt-0.5">
                                {tfn(lang, "freqMonths", months)}
                                {freq === "CUATRIMESTRAL" && <span className="ml-1 text-emerald-600 font-semibold">−5%</span>}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className={`text-[18px] font-bold ${isActive ? "text-[#161b3d]" : "text-[#1d1d1f]"}`}>
                                RD$ {price.toLocaleString()}
                              </p>
                              <p className="text-[11px] text-[#86868b]">total</p>
                            </div>
                          </div>
                          {isActive && (
                            <div className="mt-2.5 flex items-center gap-1.5">
                              <div className="w-4 h-4 rounded-full bg-[#161b3d] flex items-center justify-center flex-shrink-0">
                                <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                              </div>
                              <span className="text-[11px] font-semibold text-[#161b3d]">
                                {t(lang, "freqSelected")}
                              </span>
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                  <div className="px-6 pb-6">
                    <button
                      type="button"
                      onClick={confirmFrequency}
                      className="w-full py-3 bg-[#161b3d] text-white rounded-xl font-semibold text-[13px] uppercase tracking-wide hover:opacity-95 transition-opacity"
                    >
                      {t(lang, "freqConfirm")}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {currentStep === Step.REVIEW_PAY && (
              <section className="animate-fadeIn font-sans">
                <div className="mb-6 text-center">
                  <h2 className={`${FORUM.sectionTitle} mb-3`}>{t(lang, "choosePaymentMethod")}</h2>
                  <p className="text-[13px] text-[#86868b] max-w-xl mx-auto">
                    {t(lang, "paymentMethodDesc")}
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-8">
                  <div
                    onClick={() => setPaymentMethod("ONLINE")}
                    className={`p-10 rounded-[2.5rem] border-4 transition-all cursor-pointer group flex flex-col items-center text-center ${
                      paymentMethod === "ONLINE"
                        ? "border-[#161b3d] bg-blue-50/30 shadow-2xl scale-105"
                        : "border-gray-50 hover:border-gray-200 bg-white"
                    }`}
                  >
                    <div
                      className={`w-20 h-20 rounded-3xl mb-6 flex items-center justify-center transition-all ${
                        paymentMethod === "ONLINE"
                          ? "bg-[#161b3d] text-white"
                          : "bg-gray-100 text-gray-400 group-hover:bg-[#161b3d] group-hover:text-white"
                      }`}
                    >
                      <svg
                        className="w-10 h-10"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                        />
                      </svg>
                    </div>
                    <h3 className="text-[15px] font-semibold text-[#1d1d1f] mb-1.5 uppercase tracking-wide">
                      {t(lang, "payOnline")}
                    </h3>
                    <p className="text-[11px] text-[#86868b] font-medium uppercase tracking-wide">
                      {t(lang, "secureCard")}
                    </p>
                  </div>

                  <div
                    onClick={() => setPaymentMethod("LOCAL")}
                    className={`p-10 rounded-[2.5rem] border-4 transition-all cursor-pointer group flex flex-col items-center text-center ${
                      paymentMethod === "LOCAL"
                        ? "border-[#8b1e22] bg-red-50/30 shadow-2xl scale-105"
                        : "border-gray-50 hover:border-gray-200 bg-white"
                    }`}
                  >
                    <div
                      className={`w-20 h-20 rounded-3xl mb-6 flex items-center justify-center transition-all ${
                        paymentMethod === "LOCAL"
                          ? "bg-[#8b1e22] text-white"
                          : "bg-gray-100 text-gray-400 group-hover:bg-[#8b1e22] group-hover:text-white"
                      }`}
                    >
                      <svg
                        className="w-10 h-10"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                        />
                      </svg>
                    </div>
                    <h3 className="text-[15px] font-semibold text-[#8b1e22] mb-1.5 uppercase tracking-wide">
                      {t(lang, "payAtSchool")}
                    </h3>
                    <p className="text-[11px] text-[#86868b] font-medium uppercase tracking-wide">
                      {t(lang, "manualPayment")}
                    </p>
                  </div>
                </div>

                {paymentMethod !== null && (
                  <>
                    {/* Comprobante fiscal: always visible as open or closed box so user can reopen */}
                    <div className="max-w-xl mx-auto mt-10 mb-8 text-center">
                      {wantsComprobanteFiscal === null ? (
                        <div className="p-8 rounded-2xl shadow-sm border border-[#d2d2d7]/60 bg-[#f5f5f7] animate-fadeIn">
                          <p className="text-[15px] font-semibold text-[#1d1d1f] mb-6 tracking-tight leading-snug">
                            {t(lang, "fiscalReceiptPrompt")}
                          </p>
                          <div className="flex gap-3 justify-center flex-wrap">
                            <button
                              type="button"
                              onClick={() => setWantsComprobanteFiscal(true)}
                              className="px-6 py-3 rounded-xl font-semibold text-[13px] tracking-tight transition-all border border-[#d2d2d7] bg-white text-[#1d1d1f] hover:bg-[#f5f5f7]"
                            >
                              {t(lang, "fiscalReceiptYes")}
                            </button>
                            <button
                              type="button"
                              onClick={() => setWantsComprobanteFiscal(false)}
                              className="px-6 py-3 rounded-xl font-semibold text-[13px] tracking-tight transition-all border border-[#d2d2d7] bg-white text-[#1d1d1f] hover:bg-[#f5f5f7]"
                            >
                              {t(lang, "fiscalReceiptNo")}
                            </button>
                          </div>
                        </div>
                      ) : wantsComprobanteFiscal === false ? (
                        <div className="inline-flex flex-wrap items-center justify-center gap-1.5 py-2 px-3 rounded-xl border border-[#d2d2d7]/60 bg-[#f5f5f7]">
                          <span className="text-[12px] font-medium text-[#1d1d1f]">
                            {t(lang, "noComprobanteLabel")}
                          </span>
                          <button
                            type="button"
                            onClick={() => setWantsComprobanteFiscal(null)}
                            className="text-[11px] font-semibold underline hover:no-underline"
                            style={{ color: BRAND.navy }}
                          >
                            {t(lang, "changeComprobante")}
                          </button>
                        </div>
                      ) : (
                        <div className="rounded-2xl border border-[#d2d2d7]/60 bg-[#f5f5f7] overflow-hidden text-left">
                          <div className="px-4 py-3 border-b border-[#d2d2d7]/60 flex items-center justify-between flex-wrap gap-2">
                            <span className="text-[14px] font-semibold text-[#1d1d1f]">
                              {t(lang, "fiscalDataSection")}
                            </span>
                            <button
                              type="button"
                              onClick={() => setWantsComprobanteFiscal(false)}
                              className="text-[13px] font-semibold underline hover:no-underline"
                              style={{ color: BRAND.navy }}
                            >
                              {t(lang, "changeComprobante")}
                            </button>
                          </div>
                          <div className="p-6 max-w-2xl mx-auto space-y-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                              <div>
                                <label className={FORUM.label}>{t(lang, "fiscalNameLabel")}</label>
                                <input
                                  type="text"
                                  value={formData.fiscalName}
                                  onChange={(e) => updateFormData({ fiscalName: e.target.value })}
                                  placeholder={t(lang, "fiscalNamePlaceholder")}
                                  className={FORUM.input}
                                />
                              </div>
                              <div>
                                <label className={FORUM.label}>{t(lang, "documentTypeLabel")}</label>
                                <select
                                  value={formData.documentType}
                                  onChange={(e) => updateFormData({ documentType: e.target.value as "RNC" | "CEDULA" })}
                                  className={FORUM.input}
                                >
                                  <option value="RNC">RNC</option>
                                  <option value="CEDULA">Cédula</option>
                                </select>
                              </div>
                            </div>
                            <div>
                              <label className={FORUM.label}>{t(lang, "documentNumberLabel")}</label>
                              <input
                                type="text"
                                value={formData.documentNumber}
                                onChange={(e) => updateFormData({ documentNumber: e.target.value })}
                                placeholder={t(lang, "documentNumberPlaceholder")}
                                className={FORUM.input}
                              />
                            </div>
                            <div>
                              <label className={FORUM.label}>{t(lang, "comprobanteTypeLabel")}</label>
                              <select
                                value={formData.comprobanteType}
                                onChange={(e) => updateFormData({ comprobanteType: e.target.value as "B01" | "B02" | "B14" | "B15" })}
                                className={FORUM.input}
                              >
                                <option value="B02">B02</option>
                                <option value="B01">B01</option>
                              </select>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                  </>
                )}

                {submitError && (
                  submitError === "DUPLICATE_STUDENT_CODE" ? (
                    <div className="mt-4 max-w-xl mx-auto rounded-xl border border-amber-200 bg-amber-50/90 px-5 py-4 shadow-sm" role="alert">
                      <div className="flex gap-3">
                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center" aria-hidden>
                          <svg className="w-5 h-5 text-amber-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[14px] font-semibold text-amber-900">
                            {t(lang, "duplicateStudentCodeTitle")}
                          </p>
                          <p className="mt-1.5 text-[13px] text-amber-800 leading-snug">
                            {t(lang, "duplicateStudentCodeMessage")}
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="mt-4 text-center text-sm text-red-600" role="alert">
                      {submitError}
                    </p>
                  )
                )}

                {isAnySelectedClassFull && (
                  <p className="mt-4 text-center text-sm font-medium text-amber-700" role="alert">
                    Este curso ha alcanzado el límite de cupos disponibles.
                  </p>
                )}

                {paymentMethod === "ONLINE" && (
                  <div className="mt-8 max-w-xl mx-auto p-6 bg-white border border-[#d2d2d7]/50 rounded-2xl shadow-sm animate-fadeIn font-sans">
                    <div className="space-y-4">
                      <div className="text-center mb-2">
                        <p className="text-[11px] font-semibold text-[#86868b] uppercase tracking-wide mb-0.5">
                          {t(lang, "orderTotal")}
                        </p>
                        <p className="text-[17px] font-semibold text-[#1d1d1f]">
                          RD$ {totalPrice.toLocaleString()}
                        </p>
                      </div>

                      <button
                        type="button"
                        onClick={handleSubmitEnrollment}
                        disabled={isSubmitting || isAnySelectedClassFull}
                        className="w-full py-3.5 bg-[#161b3d] text-white rounded-xl font-semibold text-[13px] uppercase tracking-wide shadow-lg hover:opacity-95 transition-all disabled:opacity-70 disabled:pointer-events-none"
                      >
                        {isSubmitting ? (
                          <span className="flex items-center justify-center gap-2">
                            <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            {t(lang, "submitting")}
                          </span>
                        ) : (
                          t(lang, "paySecurely")
                        )}
                      </button>

                      <p className="text-[11px] text-gray-500 text-center mt-3 leading-relaxed">
                        {t(lang, "paymentTermsNote")}{" "}
                        <button
                          type="button"
                          onClick={() => setPolicyModal("terms")}
                          className="font-medium text-[#161b3d] hover:text-[#5856d6] underline-offset-2 underline"
                        >
                          {t(lang, "paymentTermsLink")}
                        </button>{" "}
                        {t(lang, "paymentTermsNoteAnd")}{" "}
                        <button
                          type="button"
                          onClick={() => setPolicyModal("refunds")}
                          className="font-medium text-[#161b3d] hover:text-[#5856d6] underline-offset-2 underline"
                        >
                          {t(lang, "paymentRefundsLink")}
                        </button>
                        .
                      </p>

                      <div className="text-center text-[11px] text-gray-500 mt-3">
                        <p className="inline-flex items-center justify-center gap-1 flex-wrap">
                          {t(lang, "paymentProcessedBy")}
                          <span className="inline-flex items-center">
                            <span className="mx-1">·</span>
                            <AzulLogo className="h-5 w-auto inline-block" />
                          </span>
                        </p>
                        <div className="mt-2 flex items-center justify-center gap-4">
                          <VisaLogo />
                          <MastercardLogo />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {paymentMethod === "LOCAL" && (
                  <div className="mt-8 max-w-xl mx-auto p-6 bg-white border border-[#d2d2d7]/50 rounded-2xl shadow-sm animate-fadeIn font-sans">
                    <div className="space-y-5">
                      <div className="text-center mb-4">
                        <p className="text-[11px] font-semibold text-[#86868b] uppercase tracking-wide mb-0.5">
                          {t(lang, "orderTotal")}
                        </p>
                        <p className="text-[17px] font-semibold text-[#1d1d1f]">
                          RD$ {totalPrice.toLocaleString()}
                        </p>
                      </div>
                      <p className="text-[13px] text-[#1d1d1f] text-center leading-relaxed">
                        {t(lang, "manualPaymentNoCupoAlert")}
                      </p>
                      <button
                        type="button"
                        onClick={handleSubmitEnrollment}
                        disabled={isSubmitting || isAnySelectedClassFull}
                        className="w-full py-3.5 bg-[#8b1e22] text-white rounded-xl font-semibold text-[13px] uppercase tracking-wide shadow-sm hover:opacity-95 active:opacity-90 transition-opacity disabled:opacity-70 disabled:pointer-events-none"
                      >
                        {isSubmitting ? (
                          <span className="flex items-center justify-center gap-2">
                            <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            {t(lang, "submitting")}
                          </span>
                        ) : (
                          t(lang, "completeEnrollment")
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </section>
            )}

            {currentStep === Step.SUCCESS && (
              <section className="animate-fadeIn text-center py-10">
                <div className="w-24 h-24 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-10 shadow-inner">
                  <svg
                    className="w-12 h-12"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="3"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <h2 className={`${FORUM.sectionTitle} mb-4`} style={{ color: BRAND.navy }}>
                  {t(lang, "enrollmentSubmitted")}
                </h2>
                <div className="max-w-lg mx-auto space-y-4 font-sans">
                  <p className="text-[14px] font-medium text-[#1d1d1f] leading-relaxed">
                    {t(lang, "thankYou")}
                  </p>
                  {formData.cardCode.trim() && (
                    <div className="mt-6 p-5 rounded-2xl border border-[#161b3d]/30 bg-white text-center">
                      <p className="text-[12px] font-semibold uppercase tracking-widest text-[#6e6e73] mb-2">
                        {lang === "es" ? "Código de matrícula" : "Student code"}
                      </p>
                      <p className="text-[28px] font-black tracking-widest" style={{ color: BRAND.navy }}>
                        {formData.cardCode.trim()}
                      </p>
                    </div>
                  )}
                  <div className="mt-8 p-5 rounded-2xl border border-[#161b3d]/20 bg-[#161b3d]/5 text-center">
                    <p className="text-[13px] text-[#6e6e73]">
                      {lang === "es"
                        ? "Recibirás un correo para configurar tu contraseña y acceder al portal de padres."
                        : "You will receive an email to set your password and access the parent portal."}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setFormData(INITIAL_FORM_DATA);
                    setCurrentStep(Step.CHILD_INFO);
                    setSubmittedRegistrationId(null);
                    setPaymentMethod(null);
                    setWantsComprobanteFiscal(null);
                    setIncludeMother(true);
                    setIncludeFather(true);
                    setSubmitError(null);
                    setStepValidationError(null);
                    setRestoredRegistrationId(null);
                    setRenewalClassesPreFilled(false);
                    setCurrentEnrolledClassIds([]);
                    setDuplicateStudentCodeOnChildStep(false);
                    lastStudentCodeRef.current = "";
                    navigate("/", { replace: true });
                  }}
                  className="mt-16 px-10 py-4 border-2 border-[#161b3d] text-[#161b3d] rounded-2xl font-black uppercase tracking-widest hover:bg-[#161b3d] hover:text-white transition-all text-xs"
                >
                  {t(lang, "returnHome")}
                </button>
              </section>
            )}
          </div>

          {stepValidationError && (
            <div className="px-8 pt-3">
              <p className="text-sm text-red-600 font-medium bg-red-50 border border-red-100 rounded-xl px-4 py-3">
                {stepValidationError}
              </p>
            </div>
          )}
          {currentStep < Step.SUCCESS && (
            <div className="bg-gray-50/50 px-8 py-6 border-t border-gray-50 flex items-center justify-between">
              {currentStep === Step.CHILD_INFO ? (
                <button
                  type="button"
                  onClick={() => navigate("/parent/login")}
                  className="px-6 py-3 rounded-xl font-semibold text-[12px] uppercase tracking-wide border-2 transition-all hover:bg-white hover:shadow-md"
                  style={{ color: BRAND.navy, borderColor: BRAND.navy }}
                >
                  {t(lang, "alreadySubmittedCta")}
                </button>
              ) : (
                <button
                  onClick={handlePrev}
                  className="px-6 py-3 rounded-xl font-semibold text-[12px] uppercase tracking-wide transition-all text-[#1d1d1f] hover:bg-white hover:shadow-md"
                >
                  &larr; {t(lang, "back")}
                </button>
              )}
              {currentStep < Step.REVIEW_PAY && (
                <button
                  type="button"
                  onClick={handleNext}
                  disabled={checkingStudentCode}
                  className="px-8 py-3 bg-[#161b3d] text-white rounded-xl font-semibold text-[12px] uppercase tracking-wide shadow-lg hover:opacity-95 transition-all disabled:opacity-70 disabled:pointer-events-none"
                >
                  {checkingStudentCode ? (lang === "es" ? "Verificando…" : "Checking…") : `${t(lang, "next")} →`}
                </button>
              )}
            </div>
          )}
        </div>
      </main>

      <footer className="mt-20 border-t border-gray-100 pt-6 pb-8 text-center text-[11px] text-gray-500 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex justify-center space-x-4 mb-3">
            <Link to="/politica-privacidad" className="hover:text-[#5856d6] transition-colors">
              {t(lang, "footerPrivacy")}
            </Link>
            <span className="text-gray-200">·</span>
            <Link to="/politica-reembolsos" className="hover:text-[#5856d6] transition-colors">
              {t(lang, "footerRefunds")}
            </Link>
            <span className="text-gray-200">·</span>
            <Link to="/terminos-uso" className="hover:text-[#5856d6] transition-colors">
              {t(lang, "footerTerms")}
            </Link>
          </div>

          <p className="font-medium">
            © 2026 American School · {schoolAddress}
          </p>
          <p className="mt-1 font-medium">
            <a
              href={`mailto:${contactEmail}`}
              className="hover:underline hover:text-[#5856d6] transition-colors"
            >
              {contactEmail}
            </a>{" "}
            · {contactPhone}
          </p>

          {currentStep === Step.CHILD_INFO && (
            <Link
              to="/admin/login"
              className="mt-6 inline-block text-[9px] text-gray-400 opacity-30 hover:opacity-100 transition-opacity"
            >
              {t(lang, "adminAccess")}
            </Link>
          )}
        </div>
      </footer>

      <PolicyModal
        open={!!policyModal}
        onClose={() => setPolicyModal(null)}
        policy={policyModal}
        lang={lang}
        onLangChange={setLang}
      />

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fadeIn { animation: fadeIn 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
      `}</style>
    </div>
  );
};

export default Forum;
